import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Table from "../../../../core/pagination/datatable";
import { Spin, Modal, Input, message } from "antd";
import Swal from "sweetalert2";
import NewWashinReason from "./NewWashinReason";
import EditWashinReason from "./EditWashinReason";
import {
  getApiBaseUrl,
  GET_WASH_IN,
  UPDATE_WASHIN_STATUS,
  ADD_ACTIVITY,
  LOGIN,
} from "../../Config/Config";
import axios from "axios";

const WashinReasons = () => {
  const user = JSON.parse(sessionStorage.getItem("user") || "{}");

  const [washinReasons, setWashInReasons] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editedReason, setEditedReason] = useState();
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [reauthVisible, setReauthVisible] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [selectedRecord, setSelectedRecord] = useState(null);

  const fetchWashInReasons = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${getApiBaseUrl()}${GET_WASH_IN}`);
      if (response.data && response.data.ResultData) {
        setWashInReasons(response.data.ResultData);
      } else {
        console.error("Invalid response:", response);
        setWashInReasons([]);
      }
    } catch (error) {
      console.error("Error fetching wash-in reasons:", error);
      setWashInReasons([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWashInReasons();
  }, []);

  const logActivity = async (isSuccess, note) => {
    try {
      await fetch(`${getApiBaseUrl()}${ADD_ACTIVITY}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ActivityType: 1,
          PerformedBy: user.UserID || 0,
          PerformedOn: new Date()
            .toISOString()
            .slice(0, 19)
            .replace("T", " "),
          Notes: note,
          Location: "WashIn Reason Master",
          IsSucces: isSuccess ? 1 : 0,
        }),
      });
    } catch (err) {
      console.error("Error logging activity:", err);
    }
  };

  const handlePasswordSubmit = async () => {
    if (!password) {
      setPasswordError("Please enter your password");
      return;
    }

    setDeleteLoading(true);
    setPasswordError("");
    const username = user?.UserName || user?.Username;

    try {
      const loginRes = await fetch(`${getApiBaseUrl()}${LOGIN}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username, password }),
      });

      const loginData = await loginRes.json();

      if (loginData?.Message === "Login successful") {
        setReauthVisible(false);
        setPassword("");
        await handleDeleteReasonConfirmed(selectedRecord);
      } else {
        setPasswordError("Reauthentication failed. Please check your password.");
        await logActivity(
          0,
          `Failed reauthentication for deleting Wash-In Reason "${selectedRecord?.Reason}" by ${username}`
        );
      }
    } catch (error) {
      console.error("Reauthentication error:", error);
      setPasswordError("Something went wrong. Please try again.");
      await logActivity(
        0,
        `Reauthentication error for deleting Wash-In Reason "${selectedRecord?.Reason}" by ${username}`
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteReasonConfirmed = async (record) => {
    const confirmResult = await Swal.fire({
      title: `Delete "${record.Reason}"?`,
      text: "This action cannot be undone.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it",
    });

    if (!confirmResult.isConfirmed) return;

    setDeleteLoading(true);

    try {
      const userId = parseInt(user?.UserID) || 0;

      const payload = {
        Id: record.Id,
        UpdatedBy: userId,
        IsActive: 0, // Soft delete
      };

      const response = await fetch(
        `${getApiBaseUrl()}${UPDATE_WASHIN_STATUS}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (result?.Status) {
        message.success(`Reason "${record.Reason}" deleted successfully`);
        await logActivity(1, `Deleted wash-in reason: "${record.Reason}"`);
      } else {
        throw new Error(result?.Message || "Failed to delete");
      }
    } catch (error) {
      console.error("Error deleting wash-in reason:", error);
      message.error("Failed to delete reason");
      await logActivity(
        0,
        `Failed to delete wash-in reason: "${record.Reason}"`
      );
    } finally {
      setDeleteLoading(false);
      fetchWashInReasons();
    }
  };

  const handleDeleteReason = (record) => {
    setSelectedRecord(record);
    setReauthVisible(true);
  };

  const handleEditClick = (record) => {
    setEditedReason({
      Id: record.Id,
      Reason: record.Reason,
      Category: record.Category,
    });
  };

  const columns = [
    {
      title: "Reason",
      dataIndex: "Reason",
      key: "Reason",
      sorter: (a, b) => a.Reason.localeCompare(b.Reason),
    },
    {
      title: "Actions",
      key: "actions",
      align: "center",
      render: (_, record) => (
        <div className="action-table-data">
          <div className="edit-delete-action d-flex align-items-center">
            <Link
              to="#"
              className="me-2"
              data-bs-toggle="offcanvas"
              data-bs-target="#EditWashInReasonOffcanvas"
              onClick={() => handleEditClick(record)}
              title="Edit"
            >
              <i data-feather="edit" className="feather-edit text-info"></i>
            </Link>
            <Link
              to="#"
              onClick={() => handleDeleteReason(record)}
              title="Delete"
            >
              <i data-feather="trash" className="feather-trash text-danger"></i>
            </Link>
          </div>
        </div>
      ),
    },
  ];

  return (
    <>
      {deleteLoading && (
        <div
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{
            zIndex: 1050,
            backdropFilter: "blur(4px)",
            backgroundColor: "rgba(255, 255, 255, 0.2)", // subtle tint
          }}
        >
          <Spin size="large" tip="Processing..." />
        </div>
      )}
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="add-item d-flex">
              <div className="page-title">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/assetmgt/dashboard">
                        <i className="fas fa-home fa-lg" />
                      </Link>
                    </li>
                    <h4 className="breadcrumb-item " aria-current="page">
                      Wash In Reasons
                    </h4>
                  </ol>
                </nav>
                <h6>Manage Wash-In Reasons</h6>
              </div>
            </div>
            <div className="page-btn">
              <Link
                data-bs-toggle="offcanvas"
                data-bs-target="#AddWashInReasonOffcanvas"
                aria-controls="AddWashInReasonOffcanvas"
                className="btn btn-success btn-sm"
              >
                <i className="ti ti-circle-plus fs-4"></i>
              </Link>
            </div>
          </div>

          <div>
            <div className="card table-list-card">
              <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                <div className="search-set mt-4"></div>
                <div className="d-flex table-dropdown my-xl-auto right-content align-items-center flex-wrap row-gap-3"></div>
              </div>
              <div className="card-body">
                <div className="table-responsive">
                  {loading ? (
                    <div className="text-center my-4">
                      <Spin size="large" />
                      <p>Loading data...</p>
                    </div>
                  ) : washinReasons.length > 0 ? (
                    <Table dataSource={washinReasons} columns={columns} />
                  ) : (
                    <p className="text-center my-4">No data available</p>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <NewWashinReason />
        <EditWashinReason editObj={editedReason} />
        <Modal
          title="Confirm Password"
          open={reauthVisible}
          onOk={handlePasswordSubmit}
          onCancel={() => {
            setReauthVisible(false);
            setPassword("");
            setPasswordError("");
            setSelectedRecord(null);
          }}
          confirmLoading={deleteLoading}
          width={350}
        >
          <Spin spinning={deleteLoading}>
            <Input.Password
              placeholder="Enter your password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
              onCopy={(e) => e.preventDefault()}
              onPaste={(e) => e.preventDefault()}
              onCut={(e) => e.preventDefault()}
            />
            {passwordError && (
              <div className="text-danger mt-2">{passwordError}</div>
            )}
          </Spin>
        </Modal>
      </div>
    </>
  );
};

export default WashinReasons;